import { Inject, Injectable } from '@angular/core';

import { BeamWidth, MeasurementUnit, Placement } from '@shure/shared/angular/data-access/system-api/models';
import { PositionUnit } from '@shure/shared/angular/utils/utils-position';

import {
	Mxa7xxLookupTableLoader,
	MXA7XX_TABLE_LOADER,
	Mxa7xxLookupTableData,
	CoveragePerspectives
} from './mxa7xx-lookup-table-loader.api';
import { Mxa7xxModel } from './mxa7xx-model';
@Injectable()
export class Mxa7xxLookupTableService {
	private tableLookupRecord: Partial<Record<Mxa7xxModel, Mxa7xxLookupTableLoader>> = {};

	constructor(@Inject(MXA7XX_TABLE_LOADER) mxa7xxLookupDataLoaderService: Mxa7xxLookupTableLoader[]) {
		mxa7xxLookupDataLoaderService.forEach((loader) => {
			this.tableLookupRecord[loader.mxa7xxModel] = loader;
		});
	}

	/**
	 * Looks up parameters of ellipse lobe
	 * @param beamWidth
	 * @param height Vertical difference between microphone and talker height in feet (ft)
	 * @param micAngle Angle of the microphone in degrees
	 * @returns Parameters of ellipse in metric (m)
	 */
	public lookupMxa7xxValues(
		model: Mxa7xxModel,
		perspective: CoveragePerspectives,
		placement: Placement,
		beamWidth: BeamWidth,
		height: PositionUnit,
		micAngle: number
	): Mxa7xxLookupTableData {
		const lookupRecord = this.getLookupRecord(model);

		// Map mic angle to the first quadrant utilizing the symmetry of lobes
		const absMicAngle = Math.abs(micAngle);
		let inputMicAngle = absMicAngle;
		if (absMicAngle > 90) {
			inputMicAngle = 90 - (absMicAngle - 90);
		}
		if (absMicAngle < -90) {
			inputMicAngle = micAngle - 180;
		}

		const convertedHeight = this.convertHeightToValidKey(lookupRecord, height);
		const convertedAngle = this.convertAngleToValidKey(lookupRecord, inputMicAngle);
		const lookupValue = lookupRecord.lookupMap
			.get(perspective)
			?.get(placement)
			?.get(beamWidth)
			?.get(convertedHeight)
			?.get(convertedAngle);

		if (!lookupValue) {
			throw new Error(
				`mxa7xx-lookup-table.service Failed to look up values for model ${model},
				perspective ${perspective}, placement ${placement}, beamWidth ${beamWidth},
				height ${convertedHeight}, angle ${convertedAngle}`
			);
		}

		// Transform the lookup value back to original quadrant based on the original mic angle and lobe symmetry
		if (micAngle >= 0 && micAngle <= 90) {
			return lookupValue;
		}
		if (micAngle <= 0 && micAngle >= -90) {
			return {
				...lookupValue,
				angle: -lookupValue.angle,
				cx: -lookupValue.cx
			};
		}
		if (micAngle > 90 && micAngle <= 180) {
			return {
				...lookupValue,
				angle: -lookupValue.angle,
				cy: -lookupValue.cy
			};
		}
		if (micAngle < -90 && micAngle >= -180) {
			return {
				...lookupValue,
				cx: -lookupValue.cx,
				cy: -lookupValue.cy
			};
		}

		// This should never happen due to the way we mapped the input angle, but added for type safety
		throw new Error(
			`mxa7xx-lookup-table.service Invalid mic angle ${micAngle}. Must be between -180 and 180 degrees.`
		);
	}

	private getLookupRecord(model: Mxa7xxModel): Mxa7xxLookupTableLoader {
		const lookupRecord = this.tableLookupRecord[model];

		if (!lookupRecord) {
			throw new Error(`mxa7xx-lookup-table.service Missing ${model} loader for MXA7XX_TABLE_LOADER`);
		}

		return lookupRecord;
	}

	private convertHeightToValidKey(lookupRecord: Mxa7xxLookupTableLoader, height: PositionUnit): number {
		//height is in feet and minimum and maximum value are governed by: this.valueLimits.limitsHeight
		const heightInFeets = height.toMeasurementUnit(MeasurementUnit.Feet);
		//transform out of bounds heights.
		if (heightInFeets > lookupRecord.valueLimits.limitsHeight.maxHeight) {
			return lookupRecord.valueLimits.limitsHeight.maxHeight;
		} else if (heightInFeets < lookupRecord.valueLimits.limitsHeight.minHeight) {
			return lookupRecord.valueLimits.limitsHeight.minHeight;
		}

		return heightInFeets;
	}

	private convertAngleToValidKey(lookupRecord: Mxa7xxLookupTableLoader, angle: number): number {
		// dng only supports angle in integers, so we round
		const roundedAngle = Math.round(angle);

		//angle is in degrees and minimum and maximum value are governed by: this.valueLimits.limitsAngle
		//transform out of bounds angles

		if (roundedAngle > lookupRecord.valueLimits.limitsAngle.maxAngle) {
			return lookupRecord.valueLimits.limitsAngle.maxAngle;
		} else if (roundedAngle < lookupRecord.valueLimits.limitsAngle.minAngle) {
			return lookupRecord.valueLimits.limitsAngle.minAngle;
		}
		return roundedAngle;
	}
}
